import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

export const GOAL_OPTIONS = [
    "Increase content speed to market without putting strains on the system",
    "Enable data driven decision making",
    "Drive consistency and accountability into ways of working",
    "Strengthen client-side leadership of the content creation ecosystem",
    "Optimize our content creation partner mix",
    "Balance the welfare of the society and the environment with economic impact within our content creation ecosystem",
    "Get the best value for our content creation investment"
];

interface GoalInputProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: string[];
    excludeOption?: string;
    placeholder?: string;
    required?: boolean;
}

const GoalInput: React.FC<GoalInputProps> = ({
    label,
    value,
    onChange,
    options,
    excludeOption,
    placeholder,
    required
}) => {
    const [showOptions, setShowOptions] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowOptions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const filteredOptions = options.filter(option => option !== excludeOption);

    return (
        <div className="relative" ref={wrapperRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {label} {required && <span className="text-red-500">*</span>}
            </label>
            <div className="relative">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    onFocus={() => setShowOptions(true)}
                    className="block w-full border border-gray-300 rounded-md pl-3 pr-10 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    required={required}
                />
                <button
                    type="button"
                    onClick={() => setShowOptions(!showOptions)}
                    className="absolute inset-y-0 right-0 px-2 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                    <ChevronDownIcon className="h-5 w-5" aria-hidden="true" />
                </button>
            </div>

            {showOptions && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => (
                            <div
                                key={index}
                                onClick={() => {
                                    onChange(option);
                                    setShowOptions(false);
                                }}
                                className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${value === option ? 'text-blue-900 bg-blue-50 font-medium' : 'text-gray-900'
                                    }`}
                            >
                                <span className="block whitespace-normal break-words">{option}</span>
                            </div>
                        ))
                    ) : (
                        <div className="py-2 px-3 text-gray-500 italic">No options available</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default GoalInput;
